"use client";

import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import * as searchOptionsActions from "member/redux/modules/searches";
import * as searchResultActions from "member/redux/modules/searchResults";
import * as bookmarkFoldersActions from "member/redux/modules/bookmarkFolders";

const PowerSearchContext = createContext();

const initialFilterState = {
  // UI state
  showResults: false,
  isMoreFiltersExpanded: false,
  currentView: "grid", // 'grid' or 'list'
  sortField: "", // e.g., 'name', 'market_cap'
  sortOrder: "desc", // 'asc' or 'desc'
  printing: false,

  // Filters
  directorNameContains: "", // For the top name search input
  keyword: "", // For the main keyword search
  matchPhrase: false,

  sourceTypes: ["company", "future_director"], // Default: Listed Companies + OpenDirector Subscribers
  candidateSource: ["all"], // Default value from legacy

  minTime: "",
  maxTime: "",
  minAge: "",
  maxAge: "",
  minRemuneration: "",
  maxRemuneration: "",
  minRevenue: "",
  maxRevenue: "",
  minBaseRem: "",
  maxBaseRem: "",
  minMarketCap: "",
  maxMarketCap: "",

  qualifications: [], // Array of simple values (like original)
  skills_and_experience: [], // Array of simple values (like original)
  australianStates: [], // Array of simple values (like original)

  companyJobTitles: [], // Array of simple values (like original)
  majorCompanyJobTitles: [],
  minorCompanyJobTitles: [],
  superannuationJobTitles: [],
  majorSuperannuationJobTitles: [],
  minorSuperannuationJobTitles: [],
  governmentJobTitles: [],
  majorGovernmentJobTitles: [],
  minorGovernmentJobTitles: [],
  otherGovernmentJobTitles: [],
  majorOtherGovernmentJobTitles: [],
  minorOtherGovernmentJobTitles: [],
  nfpJobTitles: [],
  majorNfpJobTitles: [],
  minorNfpJobTitles: [],

  industry_classifications: [], // Array of simple values (like original)
  private_classifications: [],
  superannuation_classifications: [],
  government_classifications: [],
  other_government_classifications: [],
  nfp_classifications: [],

  gender: "all", // 'all', 'male', 'female'
  companyTopOption: "all", // 'all', 'asx200', 'asx300'
  directorInLast: 1,
  executiveInLast: 1,

  // "Select All" toggles - these help manage the UI for select all buttons
  all_skills: false,
  all_qualifications: false,
  all_residency: false,
  all_classifications: {
    company: false,
    private: false,
    superannuation: false,
    government: false,
    other_government: false,
    nfp: false,
  },
  all_job_titles: {
    // Nested structure for "Select All" for job titles
    company: {
      director_positions: false,
      majorCompany: false,
      minorCompany: false,
    },
    government: {
      director_positions: false,
      majorGovernment: false,
      minorGovernment: false,
    },
    other_government: {
      director_positions: false,
      majorOtherGovernment: false,
      minorOtherGovernment: false,
    },
    superannuation: {
      director_positions: false,
      majorSuperannuation: false,
      minorSuperannuation: false,
    },
    nfp: { director_positions: false, majorNfp: false, minorNfp: false },
  },

  // Pagination
  currentPage: 1,
  // totalPages and perPage will come from Redux searchResults

  // For Worksheet params - this might be simplified or derived
  paramsForWorksheet: {},
};

const powerSearchReducer = (state, action) => {
  switch (action.type) {
    case "SET_FILTER":
      return { ...state, [action.payload.name]: action.payload.value };
    case "SET_FILTERS": // For setting multiple filters at once
      return { ...state, ...action.payload };
    case "RESET_FILTERS":
      return {
        ...initialFilterState, // Reset to initial state
        // Preserve certain UI states if needed, e.g., currentView
        currentView: state.currentView,
      };
    case "SET_PAGINATION":
      return { ...state, currentPage: action.payload.page };
    case "SET_SORT":
      return {
        ...state,
        sortField: action.payload.field,
        sortOrder: action.payload.order,
      };
    case "SET_CURRENT_VIEW":
      return { ...state, currentView: action.payload };
    case "TOGGLE_MORE_FILTERS":
      return { ...state, isMoreFiltersExpanded: !state.isMoreFiltersExpanded };
    case "SET_PRINTING":
      return { ...state, printing: action.payload };
    case "SET_PARAMS_FOR_WORKSHEET":
      return { ...state, paramsForWorksheet: action.payload };
    case "SET_SHOW_RESULTS":
      return { ...state, showResults: action.payload };
    // Add cases for specific "select all" toggles if they need complex logic beyond SET_FILTER
    case "SET_ALL_CLASSIFICATIONS_TOGGLE":
      return {
        ...state,
        all_classifications: {
          ...state.all_classifications,
          [action.payload.type]: action.payload.value,
        },
      };
    case "SET_ALL_JOB_TITLES_TOGGLE":
      return {
        ...state,
        all_job_titles: {
          ...state.all_job_titles,
          [action.payload.type]: {
            ...state.all_job_titles[action.payload.type],
            [action.payload.subtype]: action.payload.value,
          },
        },
      };
    case "RESET_SORT":
      return {
        ...state,
        sortField: "", // or your default sort field for relevance
        sortOrder: "desc", // or your default order
      };
    case "REMOVE_FILTER":
      const newState = { ...state };
      delete newState[action.payload.name];
      return newState;
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

export const PowerSearchProvider = ({ children, currentUser }) => {
  const [state, dispatchLocal] = useReducer(
    powerSearchReducer,
    initialFilterState
  );
  const reduxDispatch = useDispatch();

  // Selectors for data from Redux
  const filterOptionsData = useSelector((s) => s.searches.result); // From searchActions
  const filterOptionsLoading = useSelector((s) => s.searches.loading);

  const searchResultsData = useSelector((s) => s.searchResults.result); // From searchResultActions
  const searchResultsLoading = useSelector((s) => s.searchResults.loading);
  const allSearchResultsData = useSelector((s) => s.searchResults.all_result);
  const allSearchResultsLoading = useSelector(
    (s) => s.searchResults.allLoading
  );

  // Load initial filter options
  useEffect(() => {
    reduxDispatch(searchOptionsActions.loadSearchData());
  }, [reduxDispatch]);

  const getApiParams = useCallback(
    (isNewSearch = false) => {
      const mapToValues = (arr) =>
        Array.isArray(arr) && arr.length && typeof arr[0] === "object"
          ? arr.map((v) => v.value)
          : arr;

      // Construct params object from state, similar to legacy _getParamsFromState
      // This needs to map state values (which can be {label, value} objects) to API-expected values
      // --- Candidate Source 'all' logic ---
      const allCandidateValues = filterOptionsData?.candidate_source_options
        ? filterOptionsData.candidate_source_options
            .filter((opt) => opt.value !== "all")
            .map((opt) => opt.value)
        : [];
      const candidateSourceParam =
        state.candidateSource.length === allCandidateValues.length
          ? ["all"]
          : state.candidateSource;
      // --- End Candidate Source logic ---

      const params = {
        source_type: state.sourceTypes, // This will be sent as separate source_type[] parameters
        candidate_source: candidateSourceParam,
        min_time: state.minTime?.value,
        max_time: state.maxTime?.value,
        min_age: state.minAge?.value,
        max_age: state.maxAge?.value,
        qualifications: mapToValues(state.qualifications),
        skills_and_experience: mapToValues(state.skills_and_experience),
        industry_classifications: mapToValues(state.industry_classifications),
        private_classifications: mapToValues(state.private_classifications),
        superannuation_classifications: mapToValues(
          state.superannuation_classifications
        ),
        government_classifications: mapToValues(
          state.government_classifications
        ),
        other_government_classifications: mapToValues(
          state.other_government_classifications
        ),
        nfp_classifications: mapToValues(state.nfp_classifications),
        min_remuneration: state.minRemuneration?.value,
        max_remuneration: state.maxRemuneration?.value,
        min_base_rem: state.minBaseRem?.value,
        max_base_rem: state.maxBaseRem?.value,
        min_market_cap: state.minMarketCap?.value,
        max_market_cap: state.maxMarketCap?.value,
        min_revenue: state.minRevenue?.value,
        max_revenue: state.maxRevenue?.value,
        keyword: state.keyword,
        gender: state.gender,
        company_top_option: state.companyTopOption,
        director_in_last: state.directorInLast,
        executive_in_last: state.executiveInLast,
        company_job_titles: mapToValues(state.companyJobTitles),
        major_company_job_titles: mapToValues(state.majorCompanyJobTitles),
        minor_company_job_titles: mapToValues(state.minorCompanyJobTitles),
        superannuation_job_titles: mapToValues(state.superannuationJobTitles),
        major_superannuation_job_titles: mapToValues(
          state.majorSuperannuationJobTitles
        ),
        minor_superannuation_job_titles: mapToValues(
          state.minorSuperannuationJobTitles
        ),
        government_job_titles: mapToValues(state.governmentJobTitles),
        major_government_job_titles: mapToValues(
          state.majorGovernmentJobTitles
        ),
        minor_government_job_titles: mapToValues(
          state.minorGovernmentJobTitles
        ),
        other_government_job_titles: mapToValues(
          state.otherGovernmentJobTitles
        ),
        major_other_government_job_titles: mapToValues(
          state.majorOtherGovernmentJobTitles
        ),
        minor_other_government_job_titles: mapToValues(
          state.minorOtherGovernmentJobTitles
        ),
        nfp_job_titles: mapToValues(state.nfpJobTitles),
        major_nfp_job_titles: mapToValues(state.majorNfpJobTitles),
        minor_nfp_job_titles: mapToValues(state.minorNfpJobTitles),
        states: state.australianStates,
        director_name: state.directorNameContains,
        match_phrase: state.matchPhrase,
        page: isNewSearch ? 1 : state.currentPage,
        sort_by: isNewSearch ? "" : state.sortField,
        sort_order: isNewSearch
          ? initialFilterState.sortOrder
          : state.sortOrder,
        per_page: Number(15),
      };
      return params;
    },
    [state, filterOptionsData]
  );

  const handleSearchSubmit = useCallback(
    (isNewFormSubmit = true) => {
      if (
        !isNewFormSubmit &&
        state.directorNameContains.length > 0 &&
        state.directorNameContains.length <= 3
      ) {
        return; // Mimic legacy behavior for director name search
      }

      if (isNewFormSubmit) {
        dispatchLocal({ type: "RESET_SORT" });
        reduxDispatch(searchResultActions.clearAllSearchResultData());
        reduxDispatch(bookmarkFoldersActions.newPowerList()); // Assuming this is the correct action
      }

      const apiParams = getApiParams(isNewFormSubmit);
      dispatchLocal({ type: "SET_PARAMS_FOR_WORKSHEET", payload: apiParams });

      reduxDispatch(
        searchResultActions.loadSearchResultData(apiParams, () => {
          // Callback from legacy, e.g., to update loading state or show results
          dispatchLocal({ type: "SET_SHOW_RESULTS", payload: true });
          if (isNewFormSubmit) {
            dispatchLocal({ type: "SET_PAGINATION", payload: { page: 1 } });
          }
        })
      );
    },
    [reduxDispatch, getApiParams, state.directorNameContains]
  );

  const clearAllFilters = useCallback(() => {
    // Clear Redux state
    reduxDispatch(searchResultActions.clearAllSearchResultData());
    reduxDispatch(bookmarkFoldersActions.newPowerList());

    // Reset local state
    dispatchLocal({ type: "RESET_FILTERS" });

    // Reset pagination
    dispatchLocal({ type: "SET_PAGINATION", payload: { page: 1 } });

    // Hide results section
    dispatchLocal({ type: "SET_SHOW_RESULTS", payload: false });

    // Clear worksheet params
    dispatchLocal({
      type: "SET_PARAMS_FOR_WORKSHEET",
      payload: {},
    });
  }, [reduxDispatch]);

  const handlePageChange = useCallback(
    (newPage) => {
      dispatchLocal({ type: "SET_PAGINATION", payload: { page: newPage } });
      handleSearchSubmit(false); // Re-fetch data for the new page
    },
    [handleSearchSubmit]
  );

  const handleSortChange = useCallback((field, order) => {
    dispatchLocal({ type: "SET_SORT", payload: { field, order } });
    // handleSearchSubmit(false); // Re-fetch data with new sort
  }, []);

  // useEffect(() => {
  //   handleSearchSubmit(false);
  //   // eslint-disable-next-line
  // }, [state.sortField, state.sortOrder]);

  const fetchAllResultsForPrinting = useCallback(
    (printCallback) => {
      if (!allSearchResultsLoading) {
        const params = { ...getApiParams(false), full: true };
        if (
          !allSearchResultsData ||
          allSearchResultsData.search_results?.length === 0
        ) {
          reduxDispatch(
            searchResultActions.loadAllSearchResultData(params)
          ).then(() => {
            if (printCallback) printCallback();
          });
        } else {
          if (printCallback) printCallback();
        }
      }
    },
    [reduxDispatch, allSearchResultsLoading, allSearchResultsData, getApiParams]
  );

  const updateFilterArray = (name, values) => {
    dispatchLocal({ type: "SET_FILTER", payload: { name, value: values } });
  };

  const value = {
    // State
    ...state,
    currentUser, // Pass currentUser from props
    // Filter options from Redux
    filterOptions: filterOptionsData || {},
    filterOptionsLoading,
    // Search results from Redux
    searchResults: searchResultsData?.search_results || [],
    totalPages: searchResultsData?.pages || 0,
    itemsPerPage: Number(searchResultsData?.per_page) || 15,
    totalResults: (() => {
      // If API provides total, use it
      if (searchResultsData?.total) {
        return searchResultsData.total;
      }

      const totalPages = searchResultsData?.pages || 1;
      const currentResultsCount =
        searchResultsData?.search_results?.length || 0;

      // If only 1 page, use actual results count
      if (totalPages === 1) {
        return currentResultsCount;
      }

      // If multiple pages, calculate total
      const currentPage = searchResultsData?.page || 1;
      const itemsPerPage = Number(searchResultsData?.per_page) || 15;

      if (currentPage === totalPages) {
        // On last page: (pages-1) * per_page + current_results
        return (totalPages - 1) * itemsPerPage + currentResultsCount;
      }

      // If not on last page, estimate maximum possible
      return totalPages * itemsPerPage;
    })(),
    searchResultsLoading,
    allSearchResults: allSearchResultsData?.search_results || [],
    allSearchResultsLoading,

    // Actions / Dispatchers
    dispatchLocal, // Expose local dispatch for direct updates if necessary
    updateFilter: (name, value) => {
      if (value === null || value === undefined) {
        dispatchLocal({ type: "REMOVE_FILTER", payload: { name } });
      } else {
        dispatchLocal({ type: "SET_FILTER", payload: { name, value } });
      }
    },
    updateFilters: (payload) => dispatchLocal({ type: "SET_FILTERS", payload }),
    handleSearchSubmit,
    clearAllFilters,
    handlePageChange,
    handleSortChange,
    setCurrentView: (view) =>
      dispatchLocal({ type: "SET_CURRENT_VIEW", payload: view }),
    toggleMoreFilters: () => dispatchLocal({ type: "TOGGLE_MORE_FILTERS" }),
    setPrinting: (isPrinting) =>
      dispatchLocal({ type: "SET_PRINTING", payload: isPrinting }),
    fetchAllResultsForPrinting,
    updateAllClassificationsToggle: (type, value) =>
      dispatchLocal({
        type: "SET_ALL_CLASSIFICATIONS_TOGGLE",
        payload: { type, value },
      }),
    updateAllJobTitlesToggle: (type, subtype, value) =>
      dispatchLocal({
        type: "SET_ALL_JOB_TITLES_TOGGLE",
        payload: { type, subtype, value },
      }),
    updateFilterArray,
  };

  return (
    <PowerSearchContext.Provider value={value}>
      {children}
    </PowerSearchContext.Provider>
  );
};

export const usePowerSearch = () => {
  const context = useContext(PowerSearchContext);
  if (context === undefined) {
    throw new Error("usePowerSearch must be used within a PowerSearchProvider");
  }
  return context;
};
